/**
 * Admin screen to list, create and edit billing adjustment reason catalog entries.
 *
 * @see catalogQuery.js — multi-fetch and client-side normalization
 * @see constants.js — enum labels and default filter state
 * @see components/ — FiltersCard, FormCard, TableCard
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Container } from 'reactstrap';

import PageHeader from '../../../../components/Headers/PageHeader.jsx';
import { AdminStateContext } from '../../../../providers/AdminStateProvider.jsx';
import { useAuthToken } from '../../../../hooks/AuthHook.jsx';
import {
  createAdjustmentReason,
  getAdjustmentReasons,
  updateAdjustmentReason,
} from '../../../../api';
import { swalErrorMessage } from '../../utils';

import { fetchFilteredAdjustmentReasons } from './catalogQuery';
import {
  ADJUSTMENT_REASON_LABEL,
  REQUIRED_FORM_FIELDS,
  createDefaultFilters,
} from './constants';
import {
  buildAdjustmentReasonPayload,
  emptyForm,
} from './formHelpers';
import FiltersCard from './components/FiltersCard';
import FormCard from './components/FormCard';
import TableCard from './components/TableCard';

const AdjustmentReasons = () => {
  const [adminState] = useContext(AdminStateContext);
  const authToken = useAuthToken();
  const currentCountry = (adminState?.country || 'CL').toLowerCase();

  const [filters, setFilters] = useState(() => createDefaultFilters(currentCountry));
  const [form, setForm] = useState(() => emptyForm(currentCountry));
  const [reasons, setReasons] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = Boolean(form.id);

  // Sync country with admin navbar unless the user explicitly chose "Todos" on country.
  useEffect(() => {
    setFilters((currentFilters) => {
      if (currentFilters.country === '') {
        return currentFilters;
      }
      return {
        ...currentFilters,
        country: currentCountry,
      };
    });
    setForm((currentForm) => ({
      ...currentForm,
      country: currentCountry,
    }));
  }, [currentCountry]);

  const fetchReasons = useCallback(async () => {
    if (!authToken) return;
    setIsFetching(true);
    try {
      const catalogRows = await fetchFilteredAdjustmentReasons(
        authToken,
        filters,
        getAdjustmentReasons,
      );
      setReasons(catalogRows);
    } catch (error) {
      swalErrorMessage(error?.message || 'No se pudieron obtener los motivos.');
    } finally {
      setIsFetching(false);
    }
  }, [authToken, filters]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const handleFilterChange = (field, value) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value,
    }));
  };

  const handleFormChange = (field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm(filters.country || currentCountry));
  };

  const handleEdit = (reason) => {
    setForm({
      id: reason.id,
      label: ADJUSTMENT_REASON_LABEL,
      description: reason.description || '',
      country: reason.country || currentCountry,
      scope: reason.scope || '',
      type: reason.type || '',
      role_scope: reason.role_scope || '',
    });
  };

  const validateForm = () =>
    REQUIRED_FORM_FIELDS.every((field) => Boolean(form[field]));

  const handleSubmit = async () => {
    if (!validateForm()) {
      swalErrorMessage('Complete todos los campos antes de guardar.');
      return;
    }

    setIsSaving(true);
    try {
      const body = buildAdjustmentReasonPayload(form);

      if (isEditing) {
        await updateAdjustmentReason(authToken, form.id, body);
      } else {
        await createAdjustmentReason(authToken, body);
      }

      await Swal.fire({
        title: isEditing ? 'Motivo actualizado' : 'Motivo creado',
        text: 'El catalogo de motivos fue actualizado correctamente.',
        icon: 'success',
      });
      resetForm();
      fetchReasons();
    } catch (error) {
      swalErrorMessage(error?.message || 'No se pudo guardar el motivo.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Gestion de motivos de ajuste"
        breadcrumb={[
          { href: '#', title: 'Herramientas' },
          { href: '#', title: 'Motivos de ajuste' },
        ]}
      />
      <Container fluid>
        <FiltersCard filters={filters} onFilterChange={handleFilterChange} />
        <FormCard
          form={form}
          isEditing={isEditing}
          isSaving={isSaving}
          authToken={authToken}
          onFormChange={handleFormChange}
          onSubmit={handleSubmit}
          onCancelEdit={resetForm}
        />
        <TableCard reasons={reasons} isFetching={isFetching} onEdit={handleEdit} />
      </Container>
    </div>
  );
};

export default AdjustmentReasons;
